import { Plus, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { colorStyles, fmtHours, HU_DAY_LABELS } from "@/pages/schedule/helpers";
import DraftBlock from "@/pages/schedule/DraftBlock";

// ---------------------------------------------------------------------------
// DayList — egyetlen nap nézet
// Bal oldali kis avatar + dolgozó név + óra-összesítő, jobbra a műszakai
// kártyaként, idővel + sablon névvel + kattintásra szerkesztés.
// + gomb a sor végén nyitja a template-pickert.
// ---------------------------------------------------------------------------
export default function DayList({
  section,
  date,
  templates = [],
  selectedIds,
  onToggleSelect,
  onShiftClick,
  onAssignTemplate,
  onApproveDraft,
  onRejectDraft,
  approvingDraftId,
}) {
  if (!section) return null;
  const employees = section.employees || [];
  const dt = new Date(date + "T00:00:00");
  const dayLabel = HU_DAY_LABELS[(dt.getDay() + 6) % 7].long;
  const dateLabel = `${dt.getFullYear()}. ${String(dt.getMonth() + 1).padStart(2, "0")}. ${String(dt.getDate()).padStart(2, "0")}.`;
  const todayIso = new Date().toISOString().slice(0, 10);
  const isToday = date === todayIso;

  // Per-employee shifts + drafts for this date
  const empWithShifts = employees.map((e) => ({
    ...e,
    todayShifts: (e.shifts || []).filter((s) => s.date === date).sort(
      (a, b) => (a.start_time || "").localeCompare(b.start_time || ""),
    ),
    todayDrafts: (e.drafts || []).filter((d) => d.date === date),
  }));

  const totalHours = empWithShifts.reduce(
    (s, e) => s + e.todayShifts.reduce((a, x) => a + (Number(x.duration_hours) || 0), 0),
    0,
  );
  const scheduledCount = empWithShifts.filter((e) => e.todayShifts.length > 0).length;

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_8px_30px_-18px_rgba(15,23,42,0.16)] dark:border-border dark:bg-card">
      {/* Day header */}
      <div className={cn(
        "flex items-center gap-3 border-b border-slate-200/80 px-3 py-3 dark:border-border sm:gap-4 sm:px-5 sm:py-4",
        isToday ? "bg-indigo-50/40 dark:bg-indigo-500/[0.05]" : "bg-slate-50/40 dark:bg-foreground/[0.02]",
      )}>
        <div className={cn(
          "grid h-12 w-12 place-items-center rounded-xl font-mono text-[18px] font-light",
          isToday
            ? "bg-indigo-600 text-white shadow-[0_4px_12px_-4px_rgba(79,70,229,0.5)]"
            : "border border-slate-200 bg-white text-slate-800 dark:border-border dark:bg-card",
        )}>
          {String(dt.getDate()).padStart(2, "0")}
        </div>
        <div className="flex-1">
          <p className={cn(
            "text-[10.5px] font-semibold uppercase tracking-[0.22em]",
            isToday ? "text-indigo-600 dark:text-indigo-400" : "text-slate-500",
          )}>
            {dayLabel}{isToday && " · ma"}
          </p>
          <p className="text-base font-semibold tracking-tight">
            {dateLabel}
          </p>
        </div>
        <div className="text-right">
          <p className="font-mono text-[18px] font-light tabular-nums tracking-tight">
            {fmtHours(totalHours)}<span className="text-slate-400">h</span>
          </p>
          <p className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-slate-500">
            {scheduledCount}/{employees.length} dolgozó
          </p>
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-slate-100 dark:divide-border/60">
        {empWithShifts.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-slate-500 dark:text-muted-foreground">
            Nincs hozzárendelt dolgozó.
          </div>
        ) : (
          empWithShifts.map((emp) => (
            <EmployeeDayRow
              key={emp._id}
              employee={emp}
              date={date}
              templates={templates}
              selectedIds={selectedIds}
              onToggleSelect={onToggleSelect}
              onShiftClick={onShiftClick}
              onAssignTemplate={onAssignTemplate}
              onApproveDraft={onApproveDraft}
              onRejectDraft={onRejectDraft}
              approvingDraftId={approvingDraftId}
            />
          ))
        )}
      </div>
    </div>
  );
}

function EmployeeDayRow({
  employee,
  date,
  templates,
  selectedIds,
  onToggleSelect,
  onShiftClick,
  onAssignTemplate,
  onApproveDraft,
  onRejectDraft,
  approvingDraftId,
}) {
  const shifts = employee.todayShifts;
  const drafts = employee.todayDrafts || [];
  const initials = employee.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("");
  const hue = (initials.charCodeAt(0) * 23) % 360;

  return (
    <div className="flex items-center gap-3 px-3 py-3 transition-colors hover:bg-slate-50/60 dark:hover:bg-foreground/[0.02] sm:gap-4 sm:px-5">
      <div
        className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-[11px] font-semibold text-white shadow-sm"
        style={{
          background: `linear-gradient(135deg, hsl(${hue}deg 55% 38%), hsl(${(hue + 30) % 360}deg 60% 28%))`,
        }}
      >
        {initials || "—"}
      </div>
      <div className="min-w-0 shrink-0 sm:w-[180px]">
        <p className="truncate text-[13px] font-medium text-slate-900 dark:text-foreground">
          {employee.name}
        </p>
        {employee.position && (
          <p className="truncate text-[10.5px] text-slate-500 dark:text-muted-foreground">
            {employee.position}
          </p>
        )}
      </div>

      <div className="flex flex-1 flex-wrap items-center justify-end gap-1.5 sm:justify-start">
        {shifts.length === 0 && drafts.length === 0 ? (
          <span className="text-[11.5px] italic text-slate-300 dark:text-muted-foreground/40">
            Nincs műszak
          </span>
        ) : (
          shifts.map((sh) => {
            const cs = colorStyles(sh.color);
            const selected = selectedIds?.has(sh._id);
            return (
              <button
                key={sh._id}
                type="button"
                onClick={(e) => {
                  if (e.shiftKey || e.metaKey || e.ctrlKey) {
                    onToggleSelect?.(sh._id);
                    return;
                  }
                  onShiftClick?.(sh);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  onToggleSelect?.(sh._id);
                }}
                className={cn(
                  "group relative flex items-center gap-2 overflow-hidden rounded-md border-[0.5px] py-1 pl-2.5 pr-2 transition-all hover:-translate-y-[0.5px] hover:shadow-[0_4px_10px_-4px_rgba(15,23,42,0.2)]",
                  cs.block,
                  selected && "ring-2 ring-indigo-500/60 ring-offset-2 ring-offset-background",
                )}
                data-testid={`day-shift-${sh._id}`}
              >
                <span className={cn("h-5 w-1 rounded-full", cs.dot)} aria-hidden />
                <span className="text-[11.5px] font-semibold tracking-tight">
                  {sh.template_name}
                </span>
                <span className="font-mono text-[10.5px] tabular-nums opacity-75">
                  {sh.start_time}–{sh.end_time}
                </span>
                <span className="font-mono text-[10px] tabular-nums opacity-60">
                  · {fmtHours(sh.duration_hours)}h
                </span>
              </button>
            );
          })
        )}

        {/* Draft blocks */}
        {drafts.map((dr) => (
          <div key={dr.id || dr._id} className="min-w-[200px]">
            <DraftBlock
              draft={dr}
              loading={approvingDraftId === (dr.id || dr._id)}
              onApprove={onApproveDraft}
              onReject={onRejectDraft}
            />
          </div>
        ))}

        {/* Add shift */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="ml-1 grid h-7 w-7 place-items-center rounded-md border border-dashed border-slate-300 text-slate-400 transition-all hover:border-slate-400 hover:bg-slate-50 hover:text-slate-700 dark:border-border dark:text-muted-foreground"
              aria-label="Műszak hozzáadása"
              data-testid={`day-add-${employee._id}-${date}`}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" sideOffset={6} className="w-72 p-0">
            <DayTemplatePicker
              templates={templates}
              employeeName={employee.name}
              date={date}
              onPick={(tplId) => onAssignTemplate?.(tplId, employee._id, date)}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

function DayTemplatePicker({ templates, employeeName, date, onPick }) {
  return (
    <div className="overflow-hidden rounded-lg">
      <div className="border-b border-slate-100 bg-slate-50/60 px-3 py-2 dark:border-border dark:bg-foreground/[0.03]">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
          Műszak választás
        </p>
        <p className="mt-0.5 text-[12px] font-medium leading-tight">
          {employeeName}
        </p>
      </div>
      <div className="max-h-64 overflow-y-auto p-2">
        {templates.length === 0 ? (
          <p className="px-2 py-4 text-center text-[11px] text-slate-500">
            Nincs sablon. Hozz létre egyet a „Sablonok kezelése&rdquo; gombbal.
          </p>
        ) : (
          <div className="space-y-1">
            {templates.map((t) => {
              const cs = colorStyles(t.color);
              return (
                <button
                  key={t._id}
                  type="button"
                  onClick={() => onPick(t._id)}
                  className="group/pi relative flex w-full items-center gap-2.5 overflow-hidden rounded-md border border-transparent bg-white px-2.5 py-2 text-left transition-all hover:border-slate-200 hover:bg-slate-50"
                  data-testid={`day-pick-${t._id}`}
                >
                  <span className={cn("h-9 w-1 shrink-0 rounded-full", cs.dot)} aria-hidden />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-[12.5px] font-semibold tracking-tight">{t.name}</p>
                      <span className="font-mono text-[9.5px] font-semibold tabular-nums text-slate-500">
                        {fmtHours(t.duration_hours)}h
                      </span>
                    </div>
                    <p className="mt-0.5 font-mono text-[10.5px] tabular-nums text-slate-500">
                      <Clock className="-mt-0.5 mr-1 inline h-2.5 w-2.5 opacity-60" />
                      {t.start_time}–{t.end_time}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
